import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';

import { DEFAULT_LOCALE, appLocales } from '../../i18n';

import schema from './schema.json';

const SEO = ({ page, canonicalPage, children }) => {
  const { messages, locale } = useIntl();
  const noIndexPages = ['payment'];
  const noIndex = noIndexPages.includes(page);

  const getUrl = loc => {
    const locSeg = loc === DEFAULT_LOCALE ? '' : `${loc}/`;
    return page === 'home'
      ? `https://wallet.bitcoin.com/${locSeg}`
      : `https://wallet.bitcoin.com/${locSeg}${page}/`;
  };

  const getCanonicalUrl = loc => {
    const effectivePage = canonicalPage || page;
    const locSeg = loc === DEFAULT_LOCALE ? '' : `${loc}/`;
    return effectivePage === 'home'
      ? `https://wallet.bitcoin.com/${locSeg}`
      : `https://wallet.bitcoin.com/${locSeg}${effectivePage}/`;
  };

  const url = getUrl(locale);

  return (
    <Helmet title={messages[`${page}.meta.title`]}>
      <html lang={locale} />
      <meta name="description" content={messages[`${page}.meta.description`]} />
      <meta property="og:locale" content={locale} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={messages[`${page}.meta.title`]} />
      <meta
        property="og:description"
        content={messages[`${page}.meta.description`]}
      />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Bitcoin.com Wallet" />
      <meta
        property="og:image"
        content={`https://wallet.${
          process.env.DEPLOY_ENV === 'staging' ? 'stage.cloud.' : ''
        }bitcoin.com${messages[`${page}.meta.image`]}`}
      />
      <meta
        property="og:image:secure_url"
        content={`https://wallet.${
          process.env.DEPLOY_ENV === 'staging' ? 'stage.cloud.' : ''
        }bitcoin.com${messages[`${page}.meta.image`]}`}
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:site"
        content={`@${messages[`${page}.meta.twitter`]}`}
      />
      <meta name="twitter:title" content={messages[`${page}.meta.title`]} />
      <meta
        name="twitter:description"
        content={messages[`${page}.meta.description`]}
      />
      <meta
        name="twitter:image"
        content={`https://wallet.bitcoin.com${messages[`${page}.meta.image`]}`}
      />
      {noIndex && <meta name="robots" content="noindex" />}
      <link rel="canonical" href={getCanonicalUrl(locale)} />
      {Object.keys(appLocales).map(l => (
        <link key={l} rel="alternate" hrefLang={l} href={getCanonicalUrl(l)} />
      ))}
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      {children}
    </Helmet>
  );
};

SEO.propTypes = {
  page: PropTypes.string.isRequired,
  canonicalPage: PropTypes.string,
  children: PropTypes.node,
};

SEO.defaultProps = {
  canonicalPage: undefined,
  children: undefined,
};

export default SEO;
